import * as yup from 'yup';

import { EvictorLabelSelectorOperator } from '@cast/types-workload-eviction';

export const labelSelectorSchema = yup
  .object()
  .typeError('labelSelector should be an object')
  .noUnknown(
    true,
    'labelSelector only supports matchExpressions and matchLabels keys'
  )
  .strict()
  .shape({
    matchExpressions: yup
      .array()
      .optional()
      .of(
        yup
          .object()
          .typeError('matchExpressions should be an array')
          .noUnknown(
            true,
            'matchExpression only supports key, operator and values keys'
          )
          .strict()
          .shape({
            key: yup
              .string()
              .required('key is required for each labelSelector')
              .typeError('key should be a string'),
            operator: yup
              .string()
              .required('operator is required for each matchExpression')
              .oneOf(Object.values(EvictorLabelSelectorOperator)),
            values: yup
              .array()
              .typeError('values should be an array')
              .of(yup.string().typeError('value should be a string')),
          })
      ),
    matchLabels: yup.object().optional(),
  });

const getFlagToggleSchema = (name: string) => {
  return yup
    .object()
    .noUnknown(true, `${name} only supports enabled key`)
    .strict()
    .typeError(`${name} should be an object`)
    .optional()
    .shape({
      enabled: yup
        .boolean()
        .required('enabled key is required')
        .typeError('enabled should be a boolean'),
    });
};

export const advancedEvictorConfigurationSchema = yup
  .object()
  .noUnknown(true, 'Only evictionConfig key is supported')
  .required('Eviction configuration is required')
  .typeError('Eviction configuration should be an object')
  .strict()
  .shape({
    evictionConfig: yup
      .array()
      .required('evictionConfig is required')
      .typeError('evictionConfig should be an array')
      .of(
        yup
          .object({
            settings: yup
              .object()
              .nullable()
              .typeError('settings should be an object')
              .noUnknown(
                true,
                'settings only supports aggressive, disposable, removalDisabled and forceDisposable keys'
              )
              .strict()
              .shape({
                aggressive: getFlagToggleSchema('aggresive'),
                disposable: getFlagToggleSchema('disposable'),
                removalDisabled: getFlagToggleSchema('removalDisabled'),
                forceDisposable: getFlagToggleSchema('forceDisposable'),
              }),
            nodeSelector: yup
              .object()
              .optional()
              .typeError('nodeSelector should be an object')
              .noUnknown(true, 'nodeSelector only supports labelSelector key')
              .strict()
              .shape({
                labelSelector: labelSelectorSchema,
              }),
            podSelector: yup
              .object()
              .optional()
              .typeError('podSelector should be an object')
              .noUnknown(
                true,
                'podSelector only supports kind, labelSelector and namespace keys'
              )
              .strict()
              .shape({
                kind: yup.string().typeError('kind should be a string'),
                labelSelector: labelSelectorSchema,
                namespace: yup
                  .string()
                  .typeError('namespace should be a string'),
                replicasMin: yup
                  .number()
                  .integer()
                  .typeError('replicasMin should be an integer'),
              }),
          })
          .noUnknown(
            true,
            'evictionConfig item only supports settings, nodeSelector and podSelector keys'
          )
          .strict()
      ),
  });

export const advancedEvictorConfigPlaceholder = `evictionConfig:
- podSelector:
    namespace: "namespace"
    kind: ReplicaSet
    labelSelector:
        matchLabels:
            pod.label.key: "pod-label-value"
  settings:
    aggressive:
      enabled: true
- nodeSelector:
    labelSelector:
      matchLabels:
        node-label-key: node-label-value
  settings:
    removalDisabled:
      enabled: true`;

export const emptyConfigPlaceholder = 'evictionConfig: []';
